import { PartnerImportOption, PartnerImportResult } from '../models/partner-played.model';
import { TerpecaRoom } from '../models/room.model';
import { CombinedPrompt, applyCombinedImportRules, listedParts, roomDisplayName } from './combined-rooms.utils';

// What the voter reviews after importing from one or more tracking sites: the rooms they played (checked), the rooms
// they may have played (unchecked, grouped with the reason), and notes about what was left out. The import only ever
// adds rooms, so anything already marked is just noted.

export interface ImportReviewRoom {
  room: TerpecaRoom;
  checked: boolean;
}

export interface ImportReviewGroup {
  /** Why these rooms are only maybes (HTML: room names in bold italics). */
  reason: string;
  rooms: ImportReviewRoom[];
}

export interface ImportReview {
  played: ImportReviewRoom[];
  maybeGroups: ImportReviewGroup[];
  alreadyMarked: TerpecaRoom[];
  /** Rooms the voter disclosed an affiliation with, which they can't select. */
  affiliated: TerpecaRoom[];
  /** Rooms the sites returned that aren't finalists this year (or that we don't know). */
  notOnBallot: number;
}

export interface ImportedResult {
  label: string;
  result: PartnerImportResult;
}

function roomName(room: TerpecaRoom): string {
  return `<b><i>${roomDisplayName(room)}</i></b>`;
}

function roomList(rooms: TerpecaRoom[]): string {
  const names = rooms.map(roomName);
  return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export function buildImportReview(
    imported: ImportedResult[], roomMap: Map<string, TerpecaRoom>, sortRooms: (a: TerpecaRoom, b: TerpecaRoom) => number,
    isPlayed: (roomId: string) => boolean, isAffiliated: (roomId: string) => boolean): ImportReview {
  const definiteIds = new Set<string>();
  const maybeIds = new Set<string>();
  // Partner maybes, keyed by the partner and its listing, since one listing can cover several of our versions.
  const maybeListings = new Map<string, { label: string; partnerName?: string; ids: Set<string> }>();
  const notOnBallot = new Set<string>();
  const affiliated = new Set<string>();
  for (const { label, result } of imported) {
    for (const entry of result.rooms) {
      if (!roomMap.has(entry.terpecaID)) {
        notOnBallot.add(entry.terpecaID);
      } else if (isAffiliated(entry.terpecaID)) {
        affiliated.add(entry.terpecaID);
      } else if (!entry.maybePlayed) {
        definiteIds.add(entry.terpecaID);
      } else {
        maybeIds.add(entry.terpecaID);
        const key = `${label}\n${entry.partnerName || ''}`;
        if (!maybeListings.has(key)) {
          maybeListings.set(key, { label, partnerName: entry.partnerName, ids: new Set() });
        }
        maybeListings.get(key)?.ids.add(entry.terpecaID);
      }
    }
  }
  const combinedMaybes = applyCombinedImportRules(definiteIds, maybeIds, roomMap);
  for (const id of Array.from(definiteIds)) {
    if (isAffiliated(id)) {  // a part added for a combined game the voter is affiliated with
      definiteIds.delete(id);
      affiliated.add(id);
    }
  }

  const rooms = (ids: Iterable<string>) => Array.from(ids).map(id => roomMap.get(id)).filter(room => Boolean(room))
    .sort(sortRooms);
  const unplayed = (ids: Iterable<string>) => Array.from(ids).filter(id => !isPlayed(id));
  const unchecked = (list: TerpecaRoom[]) => list.map(room => ({ room, checked: false }));

  const maybeGroups: ImportReviewGroup[] = [];
  for (const listing of maybeListings.values()) {
    const list = rooms(unplayed(Array.from(listing.ids).filter(id => !definiteIds.has(id))));
    if (list.length) {
      maybeGroups.push({
        reason: listing.partnerName ?
          `${listing.label} can't tell which version of <b><i>${listing.partnerName}</i></b> you played. ` +
            'Check the ones you did.' :
          `${listing.label} isn't sure which version you played. Check the ones you did.`,
        rooms: unchecked(list)
      });
    }
  }
  for (const [id, prompt] of combinedMaybes) {
    const combined = roomMap.get(id);
    if (!combined || isPlayed(id) || isAffiliated(id)) {
      continue;
    }
    const parts = listedParts(combined, roomMap);
    maybeGroups.push({
      reason: prompt === CombinedPrompt.ALL_PARTS ?
        `You played ${roomList(parts)}, which can also be played together as a single experience called ` +
          `${roomName(combined)}. Check it only if you booked and played them together as a single playthrough; ` +
          'playing them separately doesn\'t count.' :
        `${roomName(parts[0])} is also part of a longer combined game, ${roomName(combined)}. Check it only if you ` +
          'played it that way, booked and played as a single playthrough.',
      rooms: unchecked([combined])
    });
  }

  const allIds = new Set([...definiteIds, ...maybeIds, ...combinedMaybes.keys()]);
  return {
    played: rooms(unplayed(definiteIds)).map(room => ({ room, checked: true })),
    maybeGroups,
    alreadyMarked: rooms(Array.from(allIds).filter(id => isPlayed(id))),
    affiliated: rooms(affiliated),
    notOnBallot: notOnBallot.size
  };
}

/** The rooms to add for what the voter checked: a checked combined game brings its unmarked listed parts along. */
export function checkedRoomIds(review: ImportReview, roomMap: Map<string, TerpecaRoom>,
                               isPlayed: (roomId: string) => boolean, isAffiliated: (roomId: string) => boolean): string[] {
  const ids = new Set<string>();
  for (const entry of [...review.played, ...review.maybeGroups.flatMap(group => group.rooms)]) {
    if (entry.checked && entry.room.docId) {
      ids.add(entry.room.docId);
      for (const part of listedParts(entry.room, roomMap)) {
        if (part.docId && !isPlayed(part.docId) && !isAffiliated(part.docId)) {
          ids.add(part.docId);
        }
      }
    }
  }
  return Array.from(ids);
}

/** Import sites in the order voters see them: Morty first (it has the most voters), then the rest by name. */
export function sortImportOptions(options: PartnerImportOption[]): PartnerImportOption[] {
  return [...options].sort((a, b) =>
    Number(b.partnerId === 'morty') - Number(a.partnerId === 'morty') || a.label.localeCompare(b.label));
}
